import React, { useContext } from 'react';
import { Box, Button, Typography, Container, Stack } from '@mui/material';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { useNavigate } from 'react-router-dom';
import { ColorModeContext } from '../../index';
import { getStoredUser, getFirstAuthorizedRoute } from '../../utils/rbac';
import ReusableHeader from '../Header/ReusableHeader';

const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--slate': '#6B7A90',
  '--critical': '#D6323A',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#1B2436',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--slate': '#93A4BD',
  '--critical': '#FF6B72',
};

export const AccessDenied = ({ featureName = 'this module', title = 'Access Denied' }) => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;
  const user = getStoredUser();

  const handleGoAuthorized = () => {
    const route = getFirstAuthorizedRoute(user);
    if (route && route !== '/no-access') {
      navigate(route);
    } else {
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authUser');
    localStorage.removeItem('token');
    localStorage.removeItem('session_Id');
    sessionStorage.clear();
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    window.location.href = '/';
  };

  const authorizedRoute = getFirstAuthorizedRoute(user);
  const hasOtherRoutes = authorizedRoute && authorizedRoute !== '/no-access';

  return (
    <Box p={2} style={V} sx={{ minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <ReusableHeader title={title} breadcrumbs={[{ label: title }]} />
      <Container maxWidth="md" sx={{ my: 'auto', py: 6 }}>
        <Box
          className="global-card"
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            border: '1px solid var(--hairline)',
            backgroundColor: 'var(--canvas)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(214, 50, 58, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <SecurityOutlinedIcon sx={{ fontSize: 38, color: 'var(--critical)' }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Calibri, Arial, sans-serif',
              fontWeight: 700,
              color: 'var(--vault-navy)',
              mb: 1.5,
            }}
          >
            Permission Required
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Calibri, Arial, sans-serif',
              fontSize: '15px',
              color: 'var(--slate)',
              maxWidth: 520,
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            Your current assigned role (<strong>{user?.role || 'Guest'}</strong>) does not have permission to view or manage{' '}
            <strong>{featureName}</strong>. If you believe this is an error, please contact your organization administrator to request access.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
            {hasOtherRoutes && (
              <Button
                variant="contained"
                onClick={handleGoAuthorized}
                endIcon={<ArrowForwardOutlinedIcon />}
                className="global-button global-button-primary"
                sx={{ px: 3, py: 1 }}
              >
                Go to Authorized Module
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<LogoutOutlinedIcon />}
              sx={{
                px: 3,
                py: 1,
                borderColor: 'var(--hairline)',
                color: 'var(--slate)',
                '&:hover': {
                  borderColor: 'var(--slate)',
                  backgroundColor: 'var(--mist)',
                },
              }}
            >
              Sign Out
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default AccessDenied;
